import { LegendKey } from "../../models/chart_model";
import { COST_UNIT } from "../../Units";
import { normalizeDisplayLabel } from "../../utility/displayText";
import { LegendStats } from "./types";

/**
 * Calculate summary statistics for each legend along with extra information from the data record
 * where the min and max values occurred.
 */
export function calculateLegendStats(
  unit: string,
  legends: LegendKey[],
  data: any[],
  extraKeys: string[] = [],
  tableData: any[] = [],
  showWtdAvg: boolean = false,
): LegendStats[] {
  function calculateLegends(legend: any) {
    const displayName = normalizeDisplayLabel(legend.name).trim();
    if (/\(forecast\)\s*$/i.test(displayName)) return displayName;

    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key),
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    // Strip any existing parenthesized unit from the name to avoid duplication
    const trailingParenMatch = displayName.match(/\(([^)]*)\)\s*$/);
    const trailingParenValue = trailingParenMatch?.[1]?.trim().toLowerCase();
    const suffixValue = String(suffix || "")
      .trim()
      .toLowerCase();
    const cleanName =
      trailingParenValue && suffixValue && trailingParenValue === suffixValue
        ? displayName.replace(/\s*\([^)]*\)\s*$/, "").trim()
        : displayName;
    return `${cleanName} (${suffix})`;
  }

  return legends.map((legend) => {
    const { dataKey } = legend;
    let minVal = Infinity;
    let maxVal = -Infinity;
    let total = 0;
    let avgTotal = 0;
    let count = 0;
    let minRecord: any = null;
    let maxRecord: any = null;

    // Weighted average calculations
    let wtdAvgTotal = 0;
    let wtdAvgCount = 0;

    data.forEach((item) => {
      const value = Number(item[dataKey]);

      if (isNaN(value)) return;

      if (value < minVal) {
        minVal = value;
        minRecord = item;
      }
      if (value > maxVal) {
        maxVal = value;
        maxRecord = item;
      }
      avgTotal += value;
      count++;

      // Calculate weighted average if showWtdAvg is true
      // Calculate weighted average if showWtdAvg is true
      if (showWtdAvg) {
        // Only calculate weighted average for Actual data
        if (dataKey === "Actual_" || dataKey.includes("Actual")) {
          // For trend tab data, construct the volume and price keys dynamically
          let volumeKey = "Actual_mcv_mw";
          let priceKey = "Actual_wt_mcp_rs_kwh";

          // If dataKey contains a date pattern (like "2025-07-01 to 2025-07-01_Actual_wt_mcp_rs_kwh")
          if (typeof dataKey === "string" && dataKey.includes("_Actual_")) {
            const datePrefix = dataKey.split("_Actual_")[0];
            volumeKey = `${datePrefix}_Actual_mcv_mw`;
            priceKey = `${datePrefix}_Actual_wt_mcp_rs_kwh`;
          }

          const volume = Number(item[volumeKey]) || 0;
          const weightedPrice = Number(item[priceKey]) || 0;

          if (volume > 0 && weightedPrice > 0) {
            wtdAvgTotal += volume * weightedPrice; // sum of (mcv * wt_mcp)
            wtdAvgCount += volume; // sum of mcv (total volume)
          }
        }
        // For Forecast data, we don't calculate weighted average - it will remain 0
      }
    });

    // Power → energy divisor is set by slot size, detected from the data's
    // max block (mirrors the chart's own detection): >48 blocks/day ⇒ 15-min
    // (÷4), otherwise 30-min (÷2). Data-driven, not assumed per series.
    let maxBlock = 0;
    for (const r of tableData.length ? tableData : data) {
      const b = Number(r?.block);
      if (!isNaN(b) && b > maxBlock) maxBlock = b;
    }
    let divideBy = maxBlock > 48 ? 4 : 2;

    let hasTotalValue = false;
    tableData.forEach((item) => {
      const value = Number(item[dataKey]);
      if (isNaN(value)) return;
      total += value;
      hasTotalValue = true;
    });
    if (!hasTotalValue) {
      data.forEach((item) => {
        const value = Number(item[dataKey]);
        if (isNaN(value)) return;
        total += value;
      });
    }

    if (
      typeof dataKey === "string" &&
      (dataKey.endsWith("_mw") || dataKey.endsWith("_kw"))
    ) {
      // Sum of per-block power (MW/KW) → energy (MWh/KWh): energy = Σ power /
      // blocks-per-hour (divideBy set above by slot size).
      total = total / divideBy;
    }

    const avg = count > 0 ? avgTotal / count : 0;
    const wtdAvg =
      showWtdAvg && wtdAvgCount > 0 ? wtdAvgTotal / wtdAvgCount : 0;

    const minExtra: Record<string, any> = {};
    const maxExtra: Record<string, any> = {};
    extraKeys.forEach((key) => {
      if (key === "Date" && legend.dateKey) {
        minExtra[key] = minRecord ? minRecord[legend.dateKey] : "-";
        maxExtra[key] = maxRecord ? maxRecord[legend.dateKey] : "-";
      } else {
        const resolvedKey =
          key === "block" && legend.blockKey ? legend.blockKey : key;
        minExtra[key] = minRecord ? (minRecord[resolvedKey] ?? "-") : "-";
        maxExtra[key] = maxRecord ? (maxRecord[resolvedKey] ?? "-") : "-";
      }
    });

    return {
      name: calculateLegends(legend),
      min: count > 0 ? minVal : 0,
      max: count > 0 ? maxVal : 0,
      avg,
      total,
      minExtra,
      maxExtra,
      unit: legend.unit || unit,
      wtdAvg: showWtdAvg
        ? dataKey === "Actual_" || dataKey.includes("Actual")
          ? wtdAvg
          : undefined
        : undefined,
    };
  });
}
